import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Building, Globe, Users, Clock, FileText, Star, MapPin,
  CheckCircle, ChevronRight, ChevronLeft, Sparkles, ArrowRight, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

const STEPS = [
  { id: 'basics', title: 'Basic Info', icon: Building, fields: ['business_name','business_type','address','city','state','zip_code','phone','email','website'] },
  { id: 'social', title: 'Social Media', icon: Users, fields: ['social_facebook','social_instagram','social_linkedin','social_youtube','social_tiktok','social_twitter'] },
  { id: 'hours', title: 'Business Hours', icon: Clock, fields: ['hours'] },
  { id: 'content', title: 'Services & Content', icon: FileText, fields: ['description','short_description','services','categories'] },
  { id: 'reviews', title: 'Review Links', icon: Star, fields: ['review_link_google','review_link_facebook','review_link_yelp'] },
  { id: 'location', title: 'Service Area', icon: MapPin, fields: ['service_area','service_area_radius','license_number'] },
];

function calcCompleteness(profile) {
  if (!profile) return 0;
  const important = [
    'business_name','business_type','address','city','state','zip_code','phone','email','website',
    'description','social_facebook','social_instagram','review_link_google',
    'categories','services','service_area',
  ];
  const filled = important.filter(k => {
    const v = profile[k];
    if (Array.isArray(v)) return v.length > 0;
    return v && String(v).trim().length > 0;
  });
  return Math.round((filled.length / important.length) * 100);
}

function Field({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div>
      <Label className="text-xs text-gray-400 mb-1 block">{label}</Label>
      <Input value={value || ''} onChange={e => onChange(e.target.value)} type={type} placeholder={placeholder}
        className="bg-gray-50 border-gray-200 text-gray-900 text-sm h-9 placeholder:text-gray-600" />
    </div>
  );
}

function ListInput({ label, items = [], onChange, placeholder }) {
  const [val, setVal] = useState('');
  const add = () => { if (val.trim()) { onChange([...items, val.trim()]); setVal(''); } };
  return (
    <div>
      <Label className="text-xs text-gray-400 mb-1 block">{label}</Label>
      <div className="flex gap-2 mb-2">
        <Input value={val} onChange={e => setVal(e.target.value)} placeholder={placeholder}
          className="bg-gray-50 border-gray-200 text-gray-900 text-sm h-8 flex-1 placeholder:text-gray-600"
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())} />
        <Button size="sm" onClick={add} className="bg-blue-600 hover:bg-blue-700 h-8 px-3 text-xs">Add</Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <Badge key={i} className="bg-gray-800 text-gray-300 text-xs gap-1 pr-1.5">
            {item}
            <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-400 ml-1">&times;</button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

export default function ClientSetupWizard({ client, getHeaders, onComplete }) {
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/business-profile`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setProfile(d.profile))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  const update = useCallback((key, val) => {
    setProfile(p => ({ ...p, [key]: val }));
  }, []);

  const updateHours = useCallback((day, field, val) => {
    setProfile(p => ({
      ...p, hours: { ...p.hours, [day]: { ...(p.hours?.[day] || {}), [field]: val } }
    }));
  }, []);

  const saveAndNext = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/client/dashboard/business-profile`, {
        method: 'PUT',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      });
      if (step < STEPS.length - 1) setStep(s => s + 1);
      else onComplete?.();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (loading || !profile) return <div className="text-gray-400 animate-pulse py-12 text-center">Loading...</div>;

  const completeness = calcCompleteness(profile);
  const currentStep = STEPS[step];

  return (
    <div className="max-w-2xl mx-auto" data-testid="setup-wizard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-400" />
            <h2 className="text-lg font-bold text-gray-900">Quick Setup</h2>
          </div>
          <p className="text-xs text-gray-500 mt-1">Complete your business profile to improve visibility</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">{completeness}%</p>
            <p className="text-[10px] text-gray-500">Complete</p>
          </div>
          {onComplete && (
            <Button variant="ghost" size="sm" onClick={onComplete} className="text-gray-500 hover:text-gray-900 h-7 px-2" data-testid="wizard-close">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <Progress value={completeness} className="h-1.5 mb-6 bg-gray-800" />

      {/* Step indicators */}
      <div className="flex gap-1 mb-6">
        {STEPS.map((s, i) => (
          <button key={s.id} onClick={() => setStep(i)}
            className={`flex-1 h-1.5 rounded-full transition-colors ${i === step ? 'bg-blue-500' : i < step ? 'bg-blue-800' : 'bg-gray-800'}`}
            data-testid={`step-indicator-${s.id}`} />
        ))}
      </div>

      {/* Step content */}
      <Card className="bg-white border-gray-200">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 rounded-lg bg-blue-900/30">
              <currentStep.icon className="h-4 w-4 text-blue-400" />
            </div>
            <div>
              <p className="text-gray-900 font-semibold text-sm">{currentStep.title}</p>
              <p className="text-[10px] text-gray-500">Step {step + 1} of {STEPS.length}</p>
            </div>
          </div>

          {/* Step: Basics */}
          {currentStep.id === 'basics' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Business Name" value={profile.business_name} onChange={v => update('business_name', v)} placeholder="Your Business Name" />
              <Field label="Business Type" value={profile.business_type} onChange={v => update('business_type', v)} placeholder="e.g., Plumbing, Restaurant" />
              <Field label="Address" value={profile.address} onChange={v => update('address', v)} className="md:col-span-2" />
              <Field label="City" value={profile.city} onChange={v => update('city', v)} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="State" value={profile.state} onChange={v => update('state', v)} />
                <Field label="ZIP" value={profile.zip_code} onChange={v => update('zip_code', v)} />
              </div>
              <Field label="Phone" value={profile.phone} onChange={v => update('phone', v)} type="tel" />
              <Field label="Email" value={profile.email} onChange={v => update('email', v)} type="email" />
              <Field label="Website" value={profile.website} onChange={v => update('website', v)} className="md:col-span-2" />
            </div>
          )}

          {/* Step: Social */}
          {currentStep.id === 'social' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Facebook" value={profile.social_facebook} onChange={v => update('social_facebook', v)} placeholder="https://facebook.com/..." />
              <Field label="Instagram" value={profile.social_instagram} onChange={v => update('social_instagram', v)} placeholder="https://instagram.com/..." />
              <Field label="LinkedIn" value={profile.social_linkedin} onChange={v => update('social_linkedin', v)} placeholder="https://linkedin.com/..." />
              <Field label="YouTube" value={profile.social_youtube} onChange={v => update('social_youtube', v)} placeholder="https://youtube.com/..." />
              <Field label="TikTok" value={profile.social_tiktok} onChange={v => update('social_tiktok', v)} placeholder="https://tiktok.com/@..." />
              <Field label="Twitter/X" value={profile.social_twitter} onChange={v => update('social_twitter', v)} placeholder="https://x.com/..." />
            </div>
          )}

          {/* Step: Hours */}
          {currentStep.id === 'hours' && (
            <div className="space-y-2">
              {DAYS.map(day => {
                const h = profile.hours?.[day] || {};
                return (
                  <div key={day} className="flex items-center gap-3">
                    <span className="text-sm text-gray-300 w-24 capitalize">{day}</span>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <div className={`w-8 h-4.5 rounded-full relative transition-colors ${!h.closed ? 'bg-blue-600' : 'bg-gray-700'}`} onClick={() => updateHours(day, 'closed', !h.closed)}>
                        <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-transform ${!h.closed ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </div>
                    </label>
                    {!h.closed ? (
                      <>
                        <Input value={h.open || ''} onChange={e => updateHours(day, 'open', e.target.value)} type="time"
                          className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 w-28" />
                        <span className="text-gray-500 text-xs">to</span>
                        <Input value={h.close || ''} onChange={e => updateHours(day, 'close', e.target.value)} type="time"
                          className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 w-28" />
                      </>
                    ) : <span className="text-xs text-gray-500">Closed</span>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Step: Content */}
          {currentStep.id === 'content' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-400 mb-1 block">Business Description</Label>
                <textarea value={profile.description || ''} onChange={e => update('description', e.target.value)} rows={3} placeholder="Tell customers about your business..."
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-md px-3 py-2 resize-none focus:border-blue-500 focus:outline-none placeholder:text-gray-600" />
              </div>
              <Field label="Short Description / Tagline" value={profile.short_description} onChange={v => update('short_description', v)} placeholder="Brief tagline..." />
              <ListInput label="Services Offered" items={profile.services || []} onChange={v => update('services', v)} placeholder="e.g., Drain Cleaning" />
              <ListInput label="Categories" items={profile.categories || []} onChange={v => update('categories', v)} placeholder="e.g., Plumbing" />
            </div>
          )}

          {/* Step: Reviews */}
          {currentStep.id === 'reviews' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-400 mb-2">Add your review page links so customers can easily leave reviews.</p>
              <Field label="Google Review Link" value={profile.review_link_google} onChange={v => update('review_link_google', v)} placeholder="https://g.page/r/..." />
              <Field label="Facebook Review Link" value={profile.review_link_facebook} onChange={v => update('review_link_facebook', v)} placeholder="https://facebook.com/.../reviews" />
              <Field label="Yelp Review Link" value={profile.review_link_yelp} onChange={v => update('review_link_yelp', v)} placeholder="https://yelp.com/biz/..." />
            </div>
          )}

          {/* Step: Location */}
          {currentStep.id === 'location' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Service Area" value={profile.service_area} onChange={v => update('service_area', v)} placeholder="e.g., Dallas-Fort Worth Metroplex" />
              <Field label="Radius" value={profile.service_area_radius} onChange={v => update('service_area_radius', v)} placeholder="e.g., 25 miles" />
              <Field label="License Number" value={profile.license_number} onChange={v => update('license_number', v)} placeholder="Optional" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-4">
        <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 0}
          className="border-gray-200 text-gray-300 hover:bg-gray-100 text-sm h-9 px-4" data-testid="wizard-prev">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex gap-2">
          {step < STEPS.length - 1 && (
            <Button variant="ghost" onClick={() => setStep(s => s + 1)} className="text-gray-500 hover:text-gray-900 text-sm h-9" data-testid="wizard-skip">
              Skip
            </Button>
          )}
          <Button onClick={saveAndNext} disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm h-9 px-5" data-testid="wizard-next">
            {saving ? 'Saving...' : step === STEPS.length - 1 ? (
              <><CheckCircle className="h-4 w-4 mr-1" /> Finish Setup</>
            ) : (
              <>Save & Continue <ChevronRight className="h-4 w-4 ml-1" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
